import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import {
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import InfoIcon from '@mui/icons-material/Info';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import CopyrightIcon from '@mui/icons-material/Copyright';
import { fetchData, deleteData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import '../shared.css';

function StockIn() {
    const [data, setData] = useState([]);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const history = useHistory();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
    const hasCreate = permissions.includes('Create Transaction');

    const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];
    const getFormattedStartDate = () => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; };

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        fetchtableData();
    }, [isAuthenticated, companyId, branchId, token, logout]);

    const fetchtableData = async () => {
        setData([]);
        fetchData('StockTransferApi', `/stockInDetails/${companyId}/${branchId}/${fromDate}/${toDate}`, token)
            .then(r => { if (r.responseCode === '1111') setData(r.response_data); })
            .catch(err => {
                console.error('Error fetching Stock In Details:', err);
                if (err.response?.status === 401) { logout(); history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`); }
            });
    };

    const handleDelete = () => {
        deleteData('StockTransferApi', `${deleteItemId}`, token)
            .then(() => { fetchtableData(); setDeleteItemId(null); setConfirmDialogOpen(false); })
            .catch(console.error);
    };

    const filtered = data.filter(item =>
        Object.values(item).some(v => String(v).toLowerCase().includes(searchQuery.toLowerCase()))
    );

    function formatDate(d) { return new Date(d).toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' }); }

    const rowBg = (item) => {
        if (!item.IsActive) return '#f5f5f5';
        if (item.InputRejected) return '#fef2f2';
        if (item.InputAccepted) return '#f0fdf4';
        return '#f5f0ff';
    };

    const statusLabel = (item) => {
        if (!item.IsActive) return { text: 'Inactive', color: '#94a3b8' };
        if (item.InputRejected) return { text: 'Rejected', color: '#dc2626' };
        if (item.InputAccepted) return { text: 'Approved', color: '#16a34a' };
        return { text: 'Waiting For Approval', color: '#7c3aed' };
    };

    return (
        <div className="pg-container">
            <div className="pg-header">
                <h5 className="pg-title">Stock Transfer In</h5>
                <div className="pg-header-actions">
                    <div className="pg-date-row">
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>From Date</label>
                            <input className="pg-input" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
                        </div>
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>To Date</label>
                            <input className="pg-input" type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
                        </div>
                        <button className="pg-btn pg-btn-outline" onClick={fetchtableData}><RefreshIcon /> Refresh</button>
                    </div>
                    <div className="pg-action-row">
                        <button className="pg-btn pg-btn-outline" onClick={() => setShowSearch(v => !v)}><SearchIcon /> Search</button>
                        {/* {hasCreate && (
                            <Link to="/StockInCreate" className="pg-btn pg-btn-primary"><AddIcon /> Create New</Link>
                        )} */}
                    </div>
                    {showSearch && (
                        <input className="pg-input pg-search-input" placeholder="Search…"
                            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} autoFocus />
                    )}
                </div>
            </div>

            <div className="pg-table-wrap">
                <table className="pg-table">
                    <thead>
                        <tr>
                            <th>#</th><th>Bill Date</th><th>Bill Number</th><th>From Branch</th>
                            <th style={{ textAlign: 'right' }}>Amount</th>
                            <th style={{ textAlign: 'right' }}>Qty</th>
                            <th>Status / Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0
                            ? <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8', padding: 16 }}>No records found</td></tr>
                            : filtered.map((item, i) => {
                                const status = statusLabel(item);
                                return (
                                    <tr key={item.Id} style={{ background: rowBg(item), opacity: item.IsActive ? 1 : 0.7 }}>
                                        <td>{i + 1}</td>
                                        <td>{formatDate(item.BillDate)}</td>
                                        <td>{item.BillPrefix}/S/{item.BillNumber}</td>
                                        <td>{item.ToBranchName}</td>
                                        <td style={{ textAlign: 'right' }}>{item.GrandTotal != null ? item.GrandTotal.toFixed(2) : '0.00'}</td>
                                        <td style={{ textAlign: 'right' }}>{item.TotalQuantity}</td>
                                        <td>
                                            <Link to={`/StockInDetail/${item.Id}`}>
                                                <IconButton size="small" title="Details">
                                                    <InfoIcon style={{ color: status.color, fontSize: 17 }} />
                                                </IconButton>
                                            </Link>
                                            <span style={{ color: status.color, fontSize: 11, fontWeight: 700, marginLeft: 4 }}>{status.text}</span>
                                        </td>
                                    </tr>
                                );
                            })
                        }
                    </tbody>
                </table>
            </div>

            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
                <DialogTitle>Delete Record?</DialogTitle>
                <DialogContent><DialogContentText>Are you sure you want to delete this record?</DialogContentText></DialogContent>
                <DialogActions>
                    <button className="pg-btn pg-btn-danger" onClick={() => setConfirmDialogOpen(false)}>Cancel</button>
                    <button className="pg-btn pg-btn-success" onClick={handleDelete}>Confirm</button>
                </DialogActions>
            </Dialog>

            <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
        </div>
    );
}

export default StockIn;
